import React, { useState } from "react";
import {
  Popover,
  Select,
  Input,
  DatePicker,
  InputNumber,
  Space,
  message,
  Badge,
  Button,
  TreeSelect
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

const { Option } = Select;

export default function BugFilterBuilderPopover({
  allBugs,
  setBugs,
  moduleOptions = [],
  moduleTreeData = []
}) {
  const [open, setOpen] = useState(false);

  const [rules, setRules] = useState([
    { id: Date.now(), field: null, operator: null, value: null }
  ]);

  const [appliedRules, setAppliedRules] = useState([]);

  const FIELDS = [
    { key: "Module", label: "Module", type: "treeselect", treeData: moduleTreeData, options: moduleOptions },
    { key: "Title", label: "Title", type: "select", options: [...new Set(allBugs.map(b => b.Title))] },
    { key: "Description", label: "Description", type: "text" },
    { key: "BugStatus", label: "Bug Status", type: "select", options: ["Open","In Progress","Closed"] },
    { key: "CREATEDBY", label: "Created By", type: "select", options: [...new Set(allBugs.map(b => b.CREATEDBY))] },
    { key: "Attachments", label: "Attachments", type: "select", options: ["Yes", "No"] },
    { key: "CREATEDTIME", label: "Created", type: "date" },
    { key: "MODIFIEDTIME", label: "Modified", type: "date" }
  ];

  const OPERATORS = {
    text: ["Contains", "Doesn't Contain", "Is", "Is Not"],
    select: ["Is", "Is Not"],
    treeselect: ["Is", "Is Not"],
    hybrid: ["Is", "Is Not", "Contains", "Doesn't Contain"],
    number: ["=", ">=", "<=", ">", "<"],
    date: ["On", "Before", "After", "Not On"]
  };

  const getFieldType = (fieldKey) =>
    FIELDS.find(f => f.key === fieldKey)?.type;

  const getFieldOperators = (fieldKey) => {
    if (fieldKey === "Description") return ["Contains", "Doesn't Contain"];
    return OPERATORS[getFieldType(fieldKey)] || [];
  }
  const updateRule = (id, key, value) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, [key]: value } : r));
  };

  const removeRule = (id) => {
    setRules(prev => prev.filter(r => r.id !== id));
  };

  const canAddNewRule = () => {
    return rules.every(r => r.field && r.operator && r.value !== null && r.value !== "");
  };

  const addRule = () => {
    if (!canAddNewRule()) {
      message.warning("Complete current filter first.");
      return;
    }

    setRules(prev => [
      ...prev,
      { id: Date.now() + Math.random(), field: null, operator: null, value: null }
    ]);
  };

  const getBugValue = (bug, field) => {
    if (field === "Attachments") return bug.Attachments?.length > 0 ? "Yes" : "No";
    return bug[field];
  };

  const applyFilters = () => {
    let data = [...allBugs];

    const validRules = rules.filter(
      r => r.field && r.operator && r.value !== null && r.value !== ""
    );

    validRules.forEach(rule => {
      const { field, operator, value } = rule;

      data = data.filter(bug => {
        const bugValue = getBugValue(bug, field);

        switch (operator) {
          case "Contains":
            return String(bugValue ?? "").toLowerCase()
              .includes(String(value).toLowerCase());

          case "Doesn't Contain":
            return !String(bugValue ?? "").toLowerCase()
              .includes(String(value).toLowerCase());

          case "Is":
            return String(bugValue) === String(value);

          case "Is Not":
            return String(bugValue) !== String(value);

          case "=":
            return bugValue == value;

          case ">=":
            return bugValue >= value;

          case "<=":
            return bugValue <= value;

          case ">":
            return bugValue > value;

          case "<":
            return bugValue < value;

          case "On":
            if (!bugValue || !value) return true;
            return dayjs(bugValue).isSame(value, "day");

          case "Not On":
            if (!bugValue || !value) return true;
            return !dayjs(bugValue).isSame(value, "day");

          case "Before":
            if (!bugValue || !value) return true;
            return dayjs(bugValue).isBefore(value, "day");

          case "After":
            if (!bugValue || !value) return true;
            return dayjs(bugValue).isAfter(value, "day");

          default:
            return true;
        }
      });
    });

    setBugs(data);
    setAppliedRules(validRules);
    setOpen(false);
  };

  const resetFilters = () => {
    setRules([{ id: Date.now(), field: null, operator: null, value: null }]);
  };

  const handleOpenChange = (visible) => {
    if (visible) {
      if (appliedRules.length > 0) {
        setRules(appliedRules);
      } else {
        setRules([{ id: Date.now(), field: null, operator: null, value: null }]);
      }
    }
    setOpen(visible);
  };

  const renderValueInput = (rule) => {
      const type = getFieldType(rule.field); 
      if (type === "hybrid") {
        if (rule.operator === "Contains" || rule.operator === "Doesn't Contain") {
          return (
            <Input
              placeholder="Enter module text"
              value={rule.value}
              onChange={e => updateRule(rule.id,"value",e.target.value)}
            /> );
        } 
        return (
          <Select
            value={rule.value}
            onChange={val => updateRule(rule.id,"value",val)}
            style={{ width: 190, height: "auto" }}
          >
            {FIELDS.find(f => f.key === rule.field)?.options?.map(opt => (
              <Option key={opt} value={opt}>{opt}</Option>
            ))}
          </Select>
        );
      } 
      if (type === "text") {
        return (
          <Input
            value={rule.value}
            onChange={e => updateRule(rule.id,"value",e.target.value)}
          />
        );
      } 
      if (type === "select") {
        const options = FIELDS.find(f => f.key === rule.field)?.options || [];
        return (
          <Select
            value={rule.value}
            onChange={val => updateRule(rule.id,"value",val)}
            style={{ width: 190, height: "auto" }}
          >
            {options.map(opt => (
              <Option key={opt} value={opt}>{opt}</Option>
            ))}
          </Select>
        );
      } 
      if (type === "treeselect") {
        const treeData = FIELDS.find(f => f.key === rule.field)?.treeData || [];
        return (
          <TreeSelect
            treeData={treeData}
            value={rule.value}
            onChange={val => updateRule(rule.id,"value",val)}
            placeholder="Select module"
            style={{ width: 190 }}
            allowClear
            treeDefaultExpandAll
          />
        );
      }
      if (type === "number") {
        return (
          <InputNumber
            min={0}
            value={rule.value}
            onChange={val => updateRule(rule.id,"value",val)}
            style={{ width: 190 }}
          />
        );
      } 
      if (type === "date") {
        return (
          <DatePicker
            value={rule.value}
            onChange={val => updateRule(rule.id,"value",val)}
            style={{ width: 190 }}
          />
        );
      } 
      return null;
    };

  const content = (
    <div style={{ width: "100%" }}>
      {rules.map(rule => (
        <Space key={rule.id} style={{ display: "flex", marginBottom: 8 }}> 
          <Select
            placeholder="Field"
            style={{ width: 140, height: "auto" }}
            value={rule.field}
            onChange={(val) => {
              updateRule(rule.id,"field",val);
              updateRule(rule.id,"operator",null);
              updateRule(rule.id,"value",null);
            }}
          >
            {FIELDS.map(f => (
              <Option key={f.key} value={f.key}>{f.label}</Option>
            ))}
          </Select> 
          <Select
            placeholder="Operator"
            style={{ width: 140, height: "auto" }}
            value={rule.operator}
            disabled={!rule.field}
            onChange={(val) => updateRule(rule.id,"operator",val)}
          >
            {(getFieldOperators(rule.field) || []).map(op => (
              <Option key={op} value={op}>{op}</Option>
            ))}
          </Select> 
          <div style={{ width: 190 }}>
            {rule.field && renderValueInput(rule)}
          </div> 
          <Button icon={<PlusOutlined />} onClick={addRule} /> 
          {rules.length > 1 && (
            <Button danger icon={<DeleteOutlined />} onClick={() => removeRule(rule.id)} />
          )}
        </Space>
      ))} 
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
        <Button onClick={resetFilters}>Reset</Button>
        <Button type="primary" onClick={applyFilters}>Apply</Button>
      </div>
    </div>
  ); 

  return (
    <Popover
      trigger="click"
      open={open}
      rootClassName="BugReportFilterLogicPopover"
      onOpenChange={handleOpenChange}
      content={content}
      placement="bottomRight"
    >
      <Badge count={appliedRules.length} showZero={false}>
            <span style={{ backgroundColor: "#0194f7", cursor: "pointer" }}>
                    <FontAwesomeIcon style={{ color: "white", width: 20 }} color="white"
                      icon={faFilter} />
            </span>
      </Badge> 
    </Popover>
  );
}